import React, { useState, useEffect, useRef } from 'react';

import {
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Portal,
  Box,
  HStack,
  Text,
} from '@chakra-ui/react';

import { CiCalendar } from 'react-icons/ci';

import DatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';

import '../fields/DateField.css';

const DateTimeField = ({
  label,
  date,
  onChange,
  required,
  htmlID,
  isDisabled,
  isClearable = false,
  minDate,
  ...rest
}: {
  label: string;
  date: Date | null;
  onChange: any;
  required?: boolean;
  htmlID?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  minDate?: Date;
  [x: string]: any;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const [timeInput, setTimeInput] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle clicks outside both the input container and the picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      setInputValue(`${year}-${month}-${day} ${hours}:${minutes}`);
      setTimeInput(`${hours}:${minutes}`);
    } else {
      setInputValue('');
      setTimeInput('');
    }
  }, [date]);

  const formatInput = (value: string) => {
    const numbers = value.replace(/[^0-9]/g, '');

    if (numbers.length <= 4) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
    } else if (numbers.length <= 8) {
      return `${numbers.slice(0, 4)}-${numbers.slice(4, 6)}-${numbers.slice(6, 8)}`;
    } else if (numbers.length <= 10) {
      return `${numbers.slice(0, 4)}-${numbers.slice(4, 6)}-${numbers.slice(6, 8)} ${numbers.slice(8)}`;
    } else {
      return `${numbers.slice(0, 4)}-${numbers.slice(4, 6)}-${numbers.slice(6, 8)} ${numbers.slice(8, 10)}:${numbers.slice(10, 12)}`;
    }
  };

  const parseDateTime = (value: string): Date | null => {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/);
    if (!match) return null;

    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);
    const hours = parseInt(match[4], 10);
    const minutes = parseInt(match[5], 10);

    if (month < 1 || month > 12 || day < 1 || day > 31 || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return null;
    }

    const date = new Date(year, month - 1, day, hours, minutes);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day ||
      date.getHours() !== hours ||
      date.getMinutes() !== minutes
    ) {
      return null;
    }

    return date;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatInput(e.target.value);
    setInputValue(formatted);

    if (formatted.length === 16) {
      const parsedDate = parseDateTime(formatted);
      if (parsedDate) {
        onChange(parsedDate);
      }
    } else if (formatted.length === 0) {
      onChange(null);
    }
  };

  const handleTimeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTimeInput(value);

    // Parse time if valid format (HH:MM)
    const timeMatch = value.match(/^(\d{2}):(\d{2})$/);
    if (timeMatch && date) {
      const hours = parseInt(timeMatch[1], 10);
      const minutes = parseInt(timeMatch[2], 10);
      
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        const newDate = new Date(date);
        newDate.setHours(hours);
        newDate.setMinutes(minutes);
        onChange(newDate);
      }
    }
  };

  const handleDatePickerChange = (selectedDate: Date | null) => {
    if (selectedDate) {
      // Parse time from timeInput if available
      const timeMatch = timeInput.match(/^(\d{2}):(\d{2})$/);
      if (timeMatch) {
        const hours = parseInt(timeMatch[1], 10);
        const minutes = parseInt(timeMatch[2], 10);
        
        if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
          selectedDate.setHours(hours);
          selectedDate.setMinutes(minutes);
        } else {
          selectedDate.setHours(0);
          selectedDate.setMinutes(0);
        }
      } else {
        // Default to 00:00 if no valid time
        selectedDate.setHours(0);
        selectedDate.setMinutes(0);
      }
    }
    onChange(selectedDate);
    setIsOpen(false);
  };

  const handleCalendarClick = () => {
    if (!isDisabled) {
      if (!isOpen && inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setPickerPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
        });
      }
      setIsOpen(!isOpen);
    }
  };

  const handleInputClick = () => {
    if (!isDisabled) {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setPickerPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
        });
      }
      setIsOpen(true);
    }
  };

  return (
    <FormControl id={htmlID} isRequired={required} isDisabled={isDisabled}>
      <FormLabel htmlFor={htmlID} ml="0">
        {label}
      </FormLabel>
      <div
        ref={containerRef}
        className="custom-date-picker"
        style={{ position: 'relative' }}
      >
        <InputGroup>
          <Input
            ref={inputRef}
            variant="main"
            value={inputValue}
            onChange={handleInputChange}
            onClick={handleInputClick}
            placeholder="YYYY-MM-DD HH:MM"
            disabled={isDisabled}
            title={isDisabled ? 'Editing is disabled' : ''}
            borderColor={!inputValue && required ? 'red' : 'inherit'}
            maxLength={16}
            {...rest}
          />
          <InputRightElement width="auto" pr={2} zIndex={0}>
            <IconButton
              aria-label="Open calendar"
              icon={<CiCalendar size={20} />}
              size="sm"
              variant="ghost"
              onClick={handleCalendarClick}
              isDisabled={isDisabled}
            />
          </InputRightElement>
        </InputGroup>
        {isOpen && (
          <Portal>
            <Box
              ref={pickerRef}
              className="custom-date-picker"
              position="absolute"
              top={`${pickerPosition.top}px`}
              left={`${pickerPosition.left}px`}
              zIndex={1400}
              bg="white"
              borderRadius="md"
              boxShadow="lg"
            >
              {/* @ts-ignore */}
              <DatePicker
                selected={date}
                onChange={handleDatePickerChange}
                inline
                dateFormat="yyyy-MM-dd"
                minDate={minDate}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                isClearable={isClearable && !isDisabled}
                placeholderText="Select date"
              />
              <Box mt={2} p={2} borderTopWidth="1px" borderColor="gray.200">
                <HStack spacing={2} align="center">
                  <Text fontSize="sm" fontWeight="medium" minW="40px">
                    Time:
                  </Text>
                  <Input
                    size="sm"
                    value={timeInput}
                    onChange={handleTimeInputChange}
                    placeholder="HH:MM"
                    maxLength={5}
                    w="100px"
                  />
                  <Text fontSize="xs" color="gray.500">
                    (24-hour format)
                  </Text>
                </HStack>
              </Box>
            </Box>
          </Portal>
        )}
      </div>
    </FormControl>
  );
};

export default DateTimeField;
